import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErr, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheckIcon, XIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/* ================================================================
 * NewCaiModal — modal de formulario
 * ================================================================ */
export default function NewCaiModal({ defaults, branches, onClose, onSaved, lockTipo = false, lockBranch = null, editing = null }) {
  // editing: autorización existente → se edita (PUT); si ya emitió documentos solo se corrigen ciertos datos
  const emitidos = editing?.documentos_emitidos || 0;
  const [zona, setZona] = useState("America/Tegucigalpa");
  const hoy = new Date().toLocaleDateString("en-CA", { timeZone: zona });   // hoy en la zona de la empresa
  const [f, setF] = useState(editing ? {
    branch_id: editing.branch_id || "",
    tipo_documento: editing.tipo_documento,
    numero_cai: editing.numero_cai || "",
    direccion_fiscal: editing.direccion_fiscal || "",
    fecha_inicio: (editing.fecha_inicio || "").slice(0, 10),
    rango_inicial: editing.rango_inicial || "",
    rango_final: editing.rango_final || "",
    fecha_limite: (editing.fecha_limite || "").slice(0, 10),
    notas: editing.notas || "",
    sustituye_id: "",
    proximo_numero: "",
    fecha_aplicacion: "",
  } : {
    branch_id: lockBranch?.id || defaults.branch_id || "",
    tipo_documento: defaults.tipo_documento || "factura",
    numero_cai: "",
    direccion_fiscal: defaults.direccion_fiscal || "",
    fecha_inicio: "",
    rango_inicial: "000-001-01-00000001",
    rango_final: "000-001-01-00000500",
    fecha_limite: "",
    notas: "",
    sustituye_id: "",
    proximo_numero: "",
    fecha_aplicacion: "",
  });
  const [saving, setSaving] = useState(false);
  const [candidatas, setCandidatas] = useState([]);
  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setZona(data.zona_horaria || "America/Tegucigalpa")).catch(() => {});
  }, []);
  // Autorizaciones que puede sustituir (vigentes o programadas del mismo documento y sucursal)
  useEffect(() => {
    if (editing || !f.branch_id || !f.tipo_documento) { setCandidatas([]); return; }
    api.get("/cai-configurations", { params: { branch_id: f.branch_id, tipo_documento: f.tipo_documento } })
      .then(({ data }) => {
        const c = (data || []).filter((x) => x.activo || x.programado);
        setCandidatas(c);
        const vig = c.find((x) => x.activo);
        setF((p) => ({ ...p, sustituye_id: vig ? vig.id : "" }));
      }).catch(() => setCandidatas([]));
  }, [f.branch_id, f.tipo_documento]);
  const enVigor = `${(f.fecha_inicio || hoy).split("-").reverse().join("/")} a las 00:00 (${zona})`;

  const setV = (k, v) => setF((prev) => {
    const next = { ...prev, [k]: v };
    // Al escribir el rango inicial, el final toma el mismo establecimiento-punto-tipo (conserva su correlativo)
    if (k === "rango_inicial" && !(editing && emitidos > 0)) {
      const a = String(v).split("-"); const b = String(prev.rango_final || "").split("-");
      if (a.length === 4 && b.length === 4 && a.slice(0, 3).every((x) => /^\d+$/.test(x)))
        next.rango_final = `${a[0]}-${a[1]}-${a[2]}-${b[3]}`;
    }
    return next;
  });
  // Nueva autorización: establecimiento / punto sugeridos = los de la sucursal (si los tiene configurados)
  useEffect(() => {
    if (editing) return;
    const br = lockBranch || (branches || []).find((b) => b.id === f.branch_id);
    const est = br?.config?.establecimiento; const pto = br?.config?.punto_emision;
    if (!est || !pto) return;
    setF((p) => {
      const a = p.rango_inicial.split("-"); const b = p.rango_final.split("-");
      return { ...p, rango_inicial: `${est}-${pto}-${a[2] || "01"}-${a[3] || "00000001"}`, rango_final: `${est}-${pto}-${b[2] || "01"}-${b[3] || "00000500"}` };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.branch_id]);

  // Al cambiar tipo, sugerimos el código SAR del rango
  useEffect(() => {
    // Guía de Remisión: se respeta el código SAR que traiga el rango autorizado.
    if (editing || f.tipo_documento === "guia_remision") return;
    const codigoSar = { factura: "01", nota_credito: "04", nota_debito: "05" }[f.tipo_documento] || "01";
    setF((prev) => {
      const [e1, p1, _t1, c1] = (prev.rango_inicial || "").split("-");
      const [e2, p2, _t2, c2] = (prev.rango_final || "").split("-");
      return {
        ...prev,
        rango_inicial: `${e1 || "000"}-${p1 || "001"}-${codigoSar}-${c1 || "00000001"}`,
        rango_final: `${e2 || "000"}-${p2 || "001"}-${codigoSar}-${c2 || "00000500"}`,
      };
    });
    // eslint-disable-next-line
  }, [f.tipo_documento]);

  const submit = async () => {
    if (!f.branch_id) { toast.error("Selecciona la sucursal de la autorización"); return; }
    if (!f.numero_cai.trim()) { toast.error("Número CAI requerido"); return; }
    if (!f.direccion_fiscal.trim()) { toast.error("Dirección fiscal requerida"); return; }
    // Ambas fechas son opcionales; si se indican, la de inicio no puede ser posterior a la límite
    if (f.fecha_inicio && f.fecha_limite && f.fecha_inicio > f.fecha_limite) { toast.error("La fecha de inicio no puede ser posterior a la fecha límite"); return; }
    setSaving(true);
    try {
      const body = { ...f, fecha_inicio: f.fecha_inicio || "", fecha_limite: f.fecha_limite || "" };
      if (editing) {
        await api.put(`/cai-configurations/${editing.id}`, body);
        toast.success("Autorización actualizada. Los documentos ya emitidos conservan sus datos.");
      } else {
        const { data } = await api.post("/cai-configurations", body);
        toast.success(data?.programado
          ? `Autorización programada: entra en vigor el ${data.entra_en_vigor || fmtDate(data.fecha_inicio)}.`
          : "Autorización registrada y vigente.");
      }
      onSaved?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const nc = useModalForm({ value: { f, zona }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="new-cai-modal" {...nc.overlayProps}>
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-agro-green/15 flex items-center justify-center">
              <ShieldCheckIcon size={20} weight="duotone" className="text-agro-green" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold">{editing ? `EDITAR AUTORIZACIÓN CAI` : "Nueva configuración de CAI"}</h2>
              <div className="text-xs text-text-muted">SAR Honduras — Constancia de Autorización de Impresión</div>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={nc.cerrar} data-testid="close-new-cai" className="text-text-muted hover:text-text-primary">
            <XIcon size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="Sucursal" req>
              {editing ? (
                <div className="w-full bg-sidebar border border-border rounded px-2 py-2 text-sm" data-testid="cai-branch-fixed">
                  {editing.branch_nombre || "—"}
                </div>
              ) : lockBranch ? (
                <div className="w-full bg-sidebar border border-border rounded px-2 py-2 text-sm" data-testid="cai-branch-fixed">
                  {lockBranch.codigo} · {lockBranch.nombre}
                </div>
              ) : (
                <select value={f.branch_id} onChange={(e) => setV("branch_id", e.target.value)}
                        data-testid="cai-branch" className="w-full bg-app border border-border rounded px-2 py-2 text-sm">
                  <option value="">— Selecciona la sucursal —</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>
                  ))}
                </select>
              )}
            </F>
            <F label="Tipo de documento" req>
              <select value={f.tipo_documento} onChange={(e) => setV("tipo_documento", e.target.value)} disabled={lockTipo || Boolean(editing)}
                      data-testid="cai-tipo" className="w-full bg-app border border-border rounded px-2 py-2 text-sm disabled:opacity-70">
                <option value="factura">Factura</option>
                <option value="nota_credito">Nota de Crédito</option>
                <option value="nota_debito">Nota de Débito</option>
              </select>
            </F>
          </div>

          <F label="Número CAI" req>
            <input
              type="text"
              value={f.numero_cai}
              onChange={(e) => setV("numero_cai", e.target.value)}
              placeholder="AAAAAA-BBBBBB-CCCCCC-DDDDDD-EEEEEE-FFFFFF-123456"
              data-testid="cai-numero"
              className="w-full bg-app border border-border rounded px-2 py-2 text-sm font-mono"
            />
          </F>

          <F label="Dirección fiscal (impresa en el documento)" req>
            <input
              type="text"
              value={f.direccion_fiscal}
              onChange={(e) => setV("direccion_fiscal", e.target.value)}
              placeholder="Ej: Boulevard Suyapa, Tegucigalpa, Honduras"
              data-testid="cai-direccion"
              className="w-full bg-app border border-border rounded px-2 py-2 text-sm"
            />
          </F>

          <div className="grid grid-cols-2 gap-3">
            <F label="Rango inicial" req>
              <input
                type="text"
                value={f.rango_inicial}
                disabled={emitidos > 0}
                title={emitidos > 0 ? "Ya emitió documentos: el rango inicial no se puede cambiar" : ""}
                onChange={(e) => setV("rango_inicial", e.target.value)}
                placeholder="000-001-01-00000001"
                data-testid="cai-rango-ini"
                className="w-full bg-app border border-border rounded px-2 py-2 text-sm font-mono"
              />
            </F>
            <F label="Rango final" req>
              <input
                type="text"
                value={f.rango_final}
                onChange={(e) => setV("rango_final", e.target.value)}
                placeholder="000-001-01-00000500"
                data-testid="cai-rango-fin"
                className="w-full bg-app border border-border rounded px-2 py-2 text-sm font-mono"
              />
            </F>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <F label="Fecha de inicio (opcional · vacía = hoy)">
              <input
                type="date"
                value={f.fecha_inicio}
                disabled={emitidos > 0}
                onChange={(e) => setV("fecha_inicio", e.target.value)}
                data-testid="cai-fecha-inicio"
                className="w-full bg-app border border-border rounded px-2 py-2 text-sm disabled:opacity-60"
              />
            </F>
            <F label="Fecha límite de emisión (opcional)">
              <input
                type="date"
                value={f.fecha_limite}
                onChange={(e) => setV("fecha_limite", e.target.value)}
                data-testid="cai-fecha-limite"
                className="w-full bg-app border border-border rounded px-2 py-2 text-sm"
              />
            </F>
            <F label="Notas (opcional)">
              <input
                type="text"
                value={f.notas}
                onChange={(e) => setV("notas", e.target.value)}
                data-testid="cai-notas"
                className="w-full bg-app border border-border rounded px-2 py-2 text-sm"
              />
            </F>
          </div>

          <div className="rounded border border-blue-500/30 bg-blue-500/5 p-3 space-y-2" data-testid="cai-inicio-emision">
            <div className="text-[10px] uppercase tracking-wider text-blue-300 font-semibold">INICIO DE EMISIÓN (OPCIONAL)</div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Próximo número a emitir">
                <input type="text" value={f.proximo_numero} onChange={(e) => setV("proximo_numero", e.target.value)}
                  placeholder={editing ? `Actual: ${String(editing.correlativo_actual || "").padStart(8, "0")}` : "Ej. 15050 o 001-001-01-00015050"}
                  data-testid="cai-proximo" className="w-full bg-app border border-border rounded px-2 py-2 text-sm font-mono" />
              </F>
              <F label="Fecha de aplicación (vacía = hoy)">
                <input type="date" value={f.fecha_aplicacion} onChange={(e) => setV("fecha_aplicacion", e.target.value)}
                  data-testid="cai-fecha-aplicacion" className="w-full bg-app border border-border rounded px-2 py-2 text-sm" />
              </F>
            </div>
            <div className="text-[11px] text-text-muted">
              Primer número que se usará al crear un documento desde esa fecha; después la numeración continúa consecutiva.
              Debe estar dentro del rango autorizado y no haber sido usado. No cambia la numeración de documentos ya registrados.
            </div>
            {editing?.inicio_programado?.numero && (
              <div className="text-[11px] text-blue-300" data-testid="cai-inicio-programado">
                Programado: desde el {fmtDate(editing.inicio_programado.fecha)} se emitirá a partir del {String(editing.inicio_programado.numero).padStart(8, "0")}.
              </div>
            )}
          </div>

          {!editing && <F label="Sustituye a (se inhabilita al entrar en vigor la nueva)">
            <select value={f.sustituye_id} onChange={(e) => setV("sustituye_id", e.target.value)} data-testid="cai-sustituye"
                    className="w-full bg-app border border-border rounded px-2 py-2 text-sm">
              <option value="">— No sustituye a ninguna —</option>
              {candidatas.map((c) => (
                <option key={c.id} value={c.id}>
                  CAI {c.numero_cai} · {c.rango_inicial} a {c.rango_final} · {c.estado}
                </option>
              ))}
            </select>
          </F>}

          {editing && emitidos > 0 && (
            <div className="text-xs rounded p-3 border border-agro-yellow/30 bg-agro-yellow/5 text-agro-yellow" data-testid="cai-edit-aviso">
              Esta autorización ya emitió {emitidos} documento(s): puedes corregir el número CAI, la dirección fiscal, la fecha
              límite, las notas y ampliar el rango final. Los documentos emitidos conservan sus datos.
            </div>
          )}
          {!f.fecha_limite && (
            <div className="text-[11px] text-text-muted" data-testid="cai-sin-limite">Sin fecha límite registrada: la autorización no vence por fecha (solo al agotar su rango o al inhabilitarla).</div>
          )}
          {!editing && <div className="text-xs rounded p-3 border border-agro-green/30 bg-agro-green/5" data-testid="cai-en-vigor">
            {f.fecha_inicio > hoy ? (
              <>Queda <b>programada</b>: entra en vigor el <b>{enVigor}</b>. Hasta entonces se sigue usando la autorización
                actual solo si continúa vigente y con numeración disponible
                {f.sustituye_id ? "; ese día la sustituida queda inhabilitada automáticamente." : "."}</>
            ) : (
              <>Entra en vigor <b>hoy</b> ({enVigor}){f.sustituye_id ? " y la sustituida queda inhabilitada al guardar" : ""}.</>
            )}
            <div className="text-[11px] text-text-muted mt-1">
              Empieza en el primer número de su rango (no continúa el correlativo anterior). No se aceptan vigencias ni
              rangos superpuestos. Los documentos ya emitidos <b>no se modifican</b>: conservan su CAI, numeración y datos.
            </div>
          </div>}
        </div>

        <div className="p-4 border-t border-border/60 flex items-center justify-end gap-2 bg-sidebar/30">
          <button onClick={nc.cerrar} className="text-xs text-text-muted hover:text-text-primary px-3 py-2">Cancelar</button>
          <button
            onClick={nc.guardar}
            disabled={(saving) || nc.saving}
            data-testid="submit-cai"
            className="bg-agro-green hover:bg-agro-green-hover disabled:opacity-40 text-black font-semibold rounded-md px-4 py-2 text-sm"
          >
            {saving ? "Guardando…" : editing ? "GUARDAR CAMBIOS" : "Guardar configuración"}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children, req }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-muted mb-1 block">
        {label} {req && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}
